// ProjectDetail.jsx
import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { wp } from "../lib/wp";


const CARD_W = 256;          // w-64
const GAP = 12;              // gap-3
const VISIBLE = 4;           // 4 küçük kart görünsün
const STRIP_INNER_W = CARD_W * VISIBLE + GAP * (VISIBLE - 1); // 1060px
const ARROW_GUTTER = 36;     // oklar için iç tarafta bırakılan pay (sol + sağ)
const STRIP_OUTER_W = STRIP_INNER_W + ARROW_GUTTER * 2;       // 1132px

const ProjectDetail = () => {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  // Küçük galeri referansları
  const stripRef = useRef(null);
  const firstItemRef = useRef(null);
  const autoTimerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    fetch(wp(`/wp/v2/project?slug=${slug}&_embed=1`))
      .then((res) => res.json())
      .then((data) => {
        if (!alive) return;
        setProject(Array.isArray(data) && data.length ? data[0] : null);
      })
      .catch(() => setProject(null));
    return () => {
      alive = false;
    };
  }, [slug]);

  // Kapak (featured image)
  const coverImage = useMemo(() => {
    const emb = project?._embedded?.["wp:featuredmedia"]?.[0];
    return emb?.media_details?.sizes?.large?.source_url || emb?.source_url || null;
  }, [project]);

  // Galeri (functions.php ile REST'e eklenen alan)
  const images = useMemo(() => {
    if (Array.isArray(project?.project_gallery) && project.project_gallery.length) {
      return project.project_gallery;
    }
    return [];
  }, [project]);

  // Metin alanlarını güvenli ve satır bazlı parse eden sürüm
  const parseProjectDescription = (description) => {
    if (!description) return {};

    // Satır sonlarını koru, HTML'i temizle
    let text = description
      .replace(/<\s*br\s*\/?>/gi, "\n") // <br> -> \n
      .replace(/<\/\s*p\s*>/gi, "\n") // </p> -> \n
      .replace(/<\s*p[^>]*>/gi, "") // <p ...> -> sil
      .replace(/&nbsp;|&#160;/gi, " ")
      .replace(/<[^>]+>/g, "") // kalan HTML'i sil
      .replace(/\r\n?|\u2028|\u2029/g, "\n")
      .replace(/[ \t]+/g, " ")
      .replace(/\n{2,}/g, "\n")
      .trim();

    // Etiketlerden sonra aynı satırdaki değeri al
    const getLineAfter = (label) => {
      const re = new RegExp(`${label}\\s*:?\\s*([^\\n]+)`, "i");
      const m = text.match(re);
      return m ? m[1].trim() : "";
    };

    const out = {
      baslik: getLineAfter("(?:Proje Başlığı|Proje Basligi|Başlık|Baslik)"),
      musteri: getLineAfter("(?:Müşteri|Musteri)"),
      konum: getLineAfter("(?:Konum|Lokasyon)"),
      yil: getLineAfter("(?:Yıl|Yil|Proje Yılı|Proje Yili)"),
      program: getLineAfter("Program"),
      alan: getLineAfter("(?:Toplam İnşaat Alanı|İnşaat Alanı|Insaat Alani|Toplam Insaat Alani)"),
    };

    // Emniyet: başka label başlıyorsa orada kes
    const ALL_LABELS =
      "Proje Başlığı|Proje Basligi|Başlık|Baslik|Müşteri|Musteri|Konum|Lokasyon|Yıl|Yil|Proje Yılı|Proje Yili|Program|Toplam İnşaat Alanı|İnşaat Alanı|Insaat Alani|Toplam Insaat Alani";
    const cutAtNextLabel = (val) => {
      if (!val) return val;
      const re = new RegExp(`\\s*(?=${ALL_LABELS}\\s*:)`, "i");
      return val.split(re)[0].trim();
    };
    Object.keys(out).forEach((k) => (out[k] = cutAtNextLabel(out[k])));

    return out;
  };

  // -------- Küçük galeri kaydırma yardımcıları --------
  const getStep = () => {
    const el = firstItemRef.current;
    if (!el) return 0;
    const style = window.getComputedStyle(el);
    const marginRight = parseFloat(style.marginRight || "0");
    return el.clientWidth + marginRight; // kart genişliği + gap
  };

  const scrollByCard = (dir = 1) => {
    const step = getStep();
    if (!stripRef.current || !step) return;
    stripRef.current.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  const startAuto = (dir) => {
    stopAuto();
    autoTimerRef.current = setInterval(() => scrollByCard(dir), 700);
  };
  const stopAuto = () => {
    if (autoTimerRef.current) {
      clearInterval(autoTimerRef.current);
      autoTimerRef.current = null;
    }
  };

  useEffect(() => () => stopAuto(), []);

  const handleModalClick = (e) => {
    if (e.target.classList.contains("custom-modal")) setSelectedImageIndex(null);
  };

  const handleKeyDown = (e) => {
    if (selectedImageIndex === null) return;
    if (e.key === "ArrowRight") {
      setSelectedImageIndex((p) => (p === images.length - 1 ? 0 : p + 1));
    } else if (e.key === "ArrowLeft") {
      setSelectedImageIndex((p) => (p === 0 ? images.length - 1 : p - 1));
    } else if (e.key === "Escape") {
      setSelectedImageIndex(null);
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedImageIndex]);

  if (!project) return <div className="p-6">Yükleniyor...</div>;
  const projectInfo = parseProjectDescription(project.project_description);

  return (
    <div className="max-w-6xl mx-auto p-6 bg-[#F8F6ED] text-gray-800 min-h-screen overflow-x-hidden">
      {/* Başlık ve bilgiler */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold mb-2 text-center uppercase tracking-wide">
          {project.title?.rendered}
        </h2>

        {/* Kapak görseli — küçük şerit gövdesi ile aynı genişlikte ve ortalı */}
        {coverImage && (
          <div className="my-6 mx-auto" style={{ width: `min(100%, ${STRIP_OUTER_W}px)` }}>
            <img
              src={coverImage}
              alt="Proje Kapak Görseli"
              className="w-full h-auto max-h-[480px] object-cover rounded-lg shadow"
            />
          </div>
        )}

        <div className="max-w-4xl mx-auto">
          <div className="space-y-3 text-base">
            {projectInfo.baslik && <Row label="Proje Başlığı" value={projectInfo.baslik} />}
            {projectInfo.musteri && <Row label="Müşteri" value={projectInfo.musteri} />}
            {(project.acf?.location || projectInfo.konum) && (
              <Row label="Konum" value={project.acf?.location || projectInfo.konum} />
            )}
            {(project.acf?.project_year || project.project_year || projectInfo.yil) && (
              <Row label="Yıl" value={project.acf?.project_year || project.project_year || projectInfo.yil} />
            )}
            {(project.acf?.program || projectInfo.program) && (
              <Row label="Program" value={project.acf?.program || projectInfo.program} />
            )}
            {(project.acf?.total_area || projectInfo.alan) && (
              <Row label="Toplam İnşaat Alanı" value={project.acf?.total_area || projectInfo.alan} />
            )}
          </div>
        </div>
      </div>

      {/* Galeri */}
      {images.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4 text-center">Proje Görselleri</h3>

          <div className="mx-auto">
            {images[0] && (
              <div className="mb-4">
                <img
                  src={images[0]}
                  alt="Ana Proje Görseli"
                  className="w-full h-auto max-h-96 object-cover rounded-lg shadow-lg cursor-pointer hover:shadow-xl transition-shadow duration-300"
                  onClick={() => setSelectedImageIndex(0)}
                />
              </div>
            )}

            {images.length > 1 && (
              // Gövde: oklar içeride, içteki şerit tam 4 kart genişliğinde
              <div className="relative mx-auto" style={{ width: `min(100%, ${STRIP_OUTER_W}px)` }}>
                {/* İnce oklar */}
                <StripArrow
                  side="left"
                  onClick={() => scrollByCard(-1)}
                  onEnter={() => startAuto(-1)}
                  onLeave={stopAuto}
                />
                <StripArrow
                  side="right"
                  onClick={() => scrollByCard(1)}
                  onEnter={() => startAuto(1)}
                  onLeave={stopAuto}
                />

                {/* 4 kartlık scroll-snap'li şerit */}
                <div
                  ref={stripRef}
                  className="mx-auto pb-4 overflow-x-auto scrollbar-hide"
                  style={{
                    width: `min(100%, ${STRIP_INNER_W}px)`,
                    scrollSnapType: "x mandatory",
                    WebkitOverflowScrolling: "touch",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                  }}
                >
                  <style jsx>{`.scrollbar-hide::-webkit-scrollbar{display:none;}`}</style>

                  <div className="flex gap-3">
                    {images.slice(1).map((src, idx) => (
                      <button
                        key={idx + 1}
                        className="flex-shrink-0 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 focus:outline-none"
                        style={{ width: CARD_W, scrollSnapAlign: "start" }}
                        onClick={() => setSelectedImageIndex(idx + 1)}
                        ref={idx === 0 ? firstItemRef : undefined}
                        type="button"
                      >
                        <img
                          src={src}
                          alt={`Proje Görseli ${idx + 2}`}
                          className="w-64 h-40 object-cover rounded-lg"
                        />
                        <span className="sr-only">
                          {idx + 2} / {images.length}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal */}
      {selectedImageIndex !== null && images.length > 0 && (
        <div
          className="custom-modal fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50"
          onClick={handleModalClick}
        >
          <button
            aria-label="Kapat"
            className="appearance-none absolute top-5 right-5 md:top-6 md:right-8 text-white hover:opacity-80 transition-opacity z-10"
            onClick={() => setSelectedImageIndex(null)}
          >
            <svg width="36" height="36" viewBox="0 0 24 24">
              <path d="M18 6L6 18M6 6l12 12" stroke="white" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>

          {images.length > 1 && (
            <ModalArrow
              side="left"
              onClick={() => setSelectedImageIndex((p) => (p === 0 ? images.length - 1 : p - 1))}
            />
          )}

          <div className="text-center">
            <img
              src={images[selectedImageIndex]}
              alt="Detay Görsel"
              className="max-h-[85vh] max-w-[90vw] rounded-lg shadow-2xl"
            />
            <p className="text-white text-lg mt-4">
              {selectedImageIndex + 1} / {images.length}
            </p>
          </div>

          {images.length > 1 && (
            <ModalArrow
              side="right"
              onClick={() =>
                setSelectedImageIndex((p) => (p === images.length - 1 ? 0 : p + 1))
              }
            />
          )}
        </div>
      )}
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex flex-col sm:flex-row">
    <span className="font-bold text-gray-700 min-w-[160px]">{label}:</span>
    <span className="text-gray-600">{value}</span>
  </div>
);

/* --- İnce dış oklar (küçük galeri için) --- */
const StripArrow = ({ side, onClick, onEnter, onLeave }) => {
  const pos = side === "left" ? "left-2" : "right-2"; // gövdenin içinde
  const d = side === "left" ? "M15 18l-6-6 6-6" : "M9 6l6 6-6 6";
  return (
    <button
      aria-label={side === "left" ? "Sola kaydır" : "Sağa kaydır"}
      className={`hidden md:flex items-center justify-center absolute ${pos} top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/30 hover:bg-black/50 transition ring-1 ring-white/40`}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onClick={onClick}
      type="button"
    >
      <svg width="18" height="18" viewBox="0 0 24 24">
        <path d={d} fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
};

/* --- Modal beyaz oklar --- */
const ModalArrow = ({ side, onClick }) => {
  const pos = side === "left" ? "left-3 md:left-8" : "right-3 md:right-8";
  const d = side === "left" ? "M15 18l-6-6 6-6" : "M9 6l6 6-6 6";
  return (
    <button
      aria-label={side === "left" ? "Önceki görsel" : "Sonraki görsel"}
      className={`appearance-none select-none touch-manipulation absolute top-1/2 -translate-y-1/2 p-0 bg-transparent border-0 z-10 ${pos}`}
      onClick={onClick}
      type="button"
    >
      <svg width="44" height="44" viewBox="0 0 24 24" className="drop-shadow-[0_0_8px_rgba(0,0,0,0.6)]">
        <path d={d} fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
};

export default ProjectDetail;
